import { Header } from '@/components/layout/Header';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

import { useAdminData } from '@/hooks/useAdminData';
import { AdminTabsList } from '@/components/admin/AdminTabsList';

// Tab Components
import { ScenarioTab } from '@/components/admin/ScenarioTab';
import { CategoriesTab, UsersTab } from '@/components/admin/SimpleAdminTabs';
import { OrgTreeTab } from '@/components/admin/OrgTreeTab';
import { CategoryForm } from '@/components/admin/CategoryTree';
import { MaterialApprovalsTab } from '@/components/admin/MaterialApprovalsTab';
import { AuditLogTab } from '@/components/admin/AuditLogTab';
import { AdminGuideTab } from '@/components/admin/AdminGuideTab';
import { NotificationsTab } from '@/components/admin/NotificationsTab';
import { MasterDataTab } from '@/components/admin/MasterDataTab';
import { PlatformSettingsTab } from '@/components/admin/PlatformSettingsTab';
import { LegalPagesTab } from '@/components/admin/LegalPagesTab';
import { UnitsTab } from '@/components/admin/UnitsTab';
import { ManufacturerTab } from '@/components/admin/ManufacturerTab';
import { MachineManufacturerTab } from '@/components/admin/MachineManufacturerTab';
import { VerticalCategoriesTab } from '@/components/admin/VerticalCategoriesTab';
import { RatingCategoriesTab } from '@/components/admin/RatingCategoriesTab';
import { FeeConfigTab } from '@/components/admin/FeeConfigTab';
import { AdminBugReportsTab } from '@/components/admin/AdminBugReportsTab';
import ApiIntegrationsAdminTab from '@/components/admin/ApiIntegrationsAdminTab';
import { SecurityTab } from '@/components/admin/SecurityTab';
import { ISO27001ReportTab } from '@/components/admin/ISO27001ReportTab';
import { ExternalCompanyForm } from '@/components/admin/AdminForms';
import { AdminDisputesTab } from '@/components/admin/AdminDisputesTab';

const AdminPage = () => {
  const admin = useAdminData();
  const {
    t, isAdmin, isGroupAdmin,
    settings, setSettings, categories, flatCategories, groups, sites, users,
    externalCompanies, pendingMaterialsList, auditData, impact, units,
    loading, saving, auditFilters, setAuditFilters, auditLoading,
    companyDialog, setCompanyDialog, categoryDialog, setCategoryDialog,
    loadData,
    handleModeChange, handleSaveSettings,
    handleApproveUser, handleApproveCompany, handleApproveMaterial,
    handleCreateGroup, handleUpdateGroup, handleDeleteGroup,
    handleCreateSite, handleUpdateSite, handleDeleteSite,
    handleRoleChange, handleDeleteUser, handleCreateCompany,
    handleCreateCategory, handleDeleteCategory,
  } = admin;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  const pendingCompanies = externalCompanies.filter((c) => c.approved_status === 'pending').length;
  const pendingUsers = users.filter((u) => !u.approved).length;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h1 className="font-manrope text-2xl font-bold mb-6" data-testid="admin-title">
          {t('admin.title')}
        </h1>

        <Tabs defaultValue="scenarios" className="space-y-6">
          <AdminTabsList
            t={t}
            isAdmin={isAdmin}
            isGroupAdmin={isGroupAdmin}
            pendingCompanies={pendingCompanies}
            pendingUsers={pendingUsers}
            pendingMaterials={pendingMaterialsList.length}
          />

          {/* Tab Contents */}
          <TabsContent value="scenarios">
            <ScenarioTab settings={settings} onModeChange={handleModeChange} onSettingsChange={setSettings} onSave={handleSaveSettings} saving={saving} t={t} impact={impact} />
          </TabsContent>

          <TabsContent value="categories">
            <CategoriesTab categories={categories}
              onAdd={() => setCategoryDialog({ open: true, parentId: null, editData: null })}
              onEdit={(cat) => setCategoryDialog({ open: true, parentId: cat.parent_node_id, editData: cat })}
              onDelete={handleDeleteCategory}
              onAddChild={(parentId) => setCategoryDialog({ open: true, parentId, editData: null })}
              t={t} />
          </TabsContent>

          <TabsContent value="orgs">
            <OrgTreeTab
              groups={groups}
              sites={sites}
              onGroupCreated={handleCreateGroup}
              onGroupUpdated={handleUpdateGroup}
              onGroupDeleted={handleDeleteGroup}
              onSiteCreated={handleCreateSite}
              onSiteUpdated={handleUpdateSite}
              onSiteDeleted={handleDeleteSite}
              externalCompanies={externalCompanies}
              onApproveCompany={handleApproveCompany}
              onAddCompany={() => setCompanyDialog({ open: true })}
              t={t}
            />
          </TabsContent>

          <TabsContent value="users">
            <UsersTab users={users} sites={sites} onApprove={handleApproveUser} onRoleChange={handleRoleChange} onDelete={handleDeleteUser} onReload={loadData} t={t} isAdmin={isAdmin} isGroupAdmin={isGroupAdmin} />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="material-approvals">
              <MaterialApprovalsTab materials={pendingMaterialsList} onApprove={handleApproveMaterial} t={t} />
            </TabsContent>
          )}

          <TabsContent value="notifications">
            <NotificationsTab t={t} />
          </TabsContent>

          {(isAdmin || isGroupAdmin) && (
            <TabsContent value="masterdata">
              <MasterDataTab t={t} />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="units">
              <UnitsTab units={units} onReload={loadData} isAdmin={isAdmin} />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="manufacturers">
              <ManufacturerTab />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="machine-manufacturers">
              <MachineManufacturerTab />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="vertical-categories">
              <VerticalCategoriesTab />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="rating-categories">
              <RatingCategoriesTab />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="fee-config">
              <FeeConfigTab />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="bug-reports">
              <AdminBugReportsTab />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="api-integrations">
              <ApiIntegrationsAdminTab />
            </TabsContent>
          )}

          <TabsContent value="audit">
            <AuditLogTab data={auditData} filters={auditFilters} loading={auditLoading}
              onFilterChange={(changes) => setAuditFilters((prev) => ({ ...prev, ...changes }))} t={t} />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="security">
              <SecurityTab />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="disputes">
              <AdminDisputesTab />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="iso27001">
              <ISO27001ReportTab />
            </TabsContent>
          )}

          <TabsContent value="guide">
            <AdminGuideTab t={t} />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="platform-settings">
              <PlatformSettingsTab t={t} />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="legal-pages">
              <LegalPagesTab t={t} />
            </TabsContent>
          )}
        </Tabs>

        {/* Dialogs */}
        <Dialog open={companyDialog.open} onOpenChange={(open) => setCompanyDialog({ open })}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('admin.addCompany')}</DialogTitle>
              <DialogDescription>{t('admin.externalCompaniesDesc')}</DialogDescription>
            </DialogHeader>
            <ExternalCompanyForm onSubmit={handleCreateCompany} onCancel={() => setCompanyDialog({ open: false })} t={t} />
          </DialogContent>
        </Dialog>

        <Dialog open={categoryDialog.open} onOpenChange={(open) => { if (!open) setCategoryDialog({ open: false, parentId: null, editData: null }); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{categoryDialog.editData ? t('admin.editCategory') : t('admin.addCategory')}</DialogTitle>
              <DialogDescription>{t('admin.categoryTreeDesc')}</DialogDescription>
            </DialogHeader>
            <CategoryForm
              flatCategories={flatCategories}
              initialData={categoryDialog.editData}
              mode={categoryDialog.editData ? 'edit' : 'create'}
              onSubmit={handleCreateCategory}
              onCancel={() => setCategoryDialog({ open: false, parentId: null, editData: null })}
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPage;
